/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import {
  getUsersActionsLogs as getUsersReportSearchLogs,
  getUsersLogDetails,
} from '../services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {
  mapRouteToReportTypeLabel,
  mapUsersReportRows,
} from './reportTypeOptions'
import { getModalFields } from './getModalFields'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AuditLogDetailsModal from '../../AuditLogDetailsModal'
import AuditUsersReportFilters from './AuditUsersReportFilters'
import {
  SearchLogResponse,
  UsersReportSearchLogRow,
} from '../interfaces'

const limit = 15

export default function ListAllAuditUsersReport() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const action = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const originType = useHookstate<'users' | 'station' | null>(null)
  const selectedUsers = useHookstate<number | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandUsersReports = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] =
    useState<UsersReportSearchLogRow | null>(null)

  const [loadingRowId, setLoadingRowId] = useState<number | null>(null) // Novo state para loading do row específico
  const [openTooltips, setOpenTooltips] = useState<Record<number, boolean>>({})

  // mapActionType movida para reportTypeOptions.ts (import acima)
  const columns: readonly GridColDef[] = [
    {
      field: 'user:representation_name',
      headerName: translate('Usuário'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'route',
      headerName: translate('action'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (value) =>
        mapRouteToReportTypeLabel(translate, value as string),
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) =>
        dayjs(value as string).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip
            title={translate('Visualizar')}
            open={openTooltips[String((params.row as UsersReportSearchLogRow).id)] ?? false}
            onOpen={() =>
              setOpenTooltips(prev => ({
                ...prev,
                [String((params.row as UsersReportSearchLogRow).id)]: true,
              }))
            }
            onClose={() =>
              setOpenTooltips(prev => ({
                ...prev,
                [String((params.row as UsersReportSearchLogRow).id)]: false,
              }))
            }
          >
            <IconButton
              onClick={() => {
                setOpenTooltips(prev => ({
                  ...prev,
                  [String((params.row as UsersReportSearchLogRow).id)]: false,
                })) // Força o fechamento do tooltip ao clicar
                handleView(params.row as UsersReportSearchLogRow)
              }}
              size="small"
              aria-label={translate('Visualizar')}
              disabled={
                loadingRowId === (params.row as UsersReportSearchLogRow).id
              }
            >
              {loadingRowId === (params.row as UsersReportSearchLogRow).id ? (
                <CircularProgress size={20} />
              ) : (
                <VisibilityIcon />
              )}
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    action.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandUsersReports.set(false)
  }

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    if (!startDate || !finishDate) {
      notify({
        type: 'error',
        message: translate('Start date and end date must be provided'),
      })
      return false
    }

    loadingButton.set(true)

    const page = model.page
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    const searchLogData: SearchLogResponse = await getUsersReportSearchLogs({
      user_id: userId.get() || undefined,
      start_date: startDate,
      finish_date: finishDate,
      action: action.get(),
      page: page,
      limit: pageSize,
      orders:
        orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
    }).finally(() => {
      loadingButton.set(false)
      expandUsersReports.set(true)
    })

    // Usar a função dedicada para mapear as linhas
    setRows(mapUsersReportRows(searchLogData.data, page, pageSize, translate))

    setRowCount(searchLogData.rowsCount)
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  const handleView = async (row: UsersReportSearchLogRow) => {
    setLoadingRowId(row.id ?? null)
    try {
      if (row.id == null) {
        notify({
          type: 'error',
          message: translate('Invalid log id'),
        })
        return
      }
      const details = await getUsersLogDetails(Number(row.id))
      setSelectedRow(details as UsersReportSearchLogRow)
      setOpenModal(true)
    } catch (error) {
      notify({
        type: 'error',
        message: translate('Erro ao carregar detalhes do log'),
      })
    } finally {
      setLoadingRowId(null)
    }
  }

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditUsersReportFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          focusChange={focusChange}
          resetValues={resetValues}
          userId={userId}
          initialPeriod={initialPeriod}
          action={action}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
          originType={originType}
          selectedDevice={selectedUsers}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() => expandUsersReports.set(!expandUsersReports.get())}
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandUsersReports.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandUsersReports.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <AuditLogDetailsModal
        open={openModal}
        onClose={(event?: unknown, reason?: string) => {
          if (reason !== 'backdropClick') {
            setOpenModal(false)
          }
        }}
        selectedRow={selectedRow}
        fieldsToDisplay={getModalFields(selectedRow, translate)}
      />
    </>
  )
}
